import React, { useEffect, useState, useCallback } from 'react';
import { api } from '../../api';
import { useToast } from '../../ToastContext';
import { SELLER_PLAN, planLabel, planPrice } from '../../pricing';
import { formatCurrency } from '../../currencyUtil';

const tierLabel = planLabel;
const priceLabel = account => {
  const n = account.location_count || 1;
  return `$${formatCurrency(planPrice(n))}/mo (${n} location${n !== 1 ? 's' : ''})`;
};

function daysLeft(expiration) {
  if (!expiration) return null;
  return Math.ceil((new Date(expiration) - new Date()) / 86400000);
}

function StatusBadge({ tier, expiration }) {
  if (!tier) return (
    <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-slate-100 text-slate-500 dark:bg-slate-700 dark:text-slate-400">
      None
    </span>
  );
  const days = daysLeft(expiration);
  const expired = days !== null && days <= 0;
  const warning = days !== null && days <= 7 && !expired;
  if (expired) return (
    <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400">
      Expired
    </span>
  );
  if (warning) return (
    <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400">
      {tierLabel(tier)} · {days}d left
    </span>
  );
  return (
    <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400">
      {tierLabel(tier)}
    </span>
  );
}

function PendingRow({ account, onApproved, onRejected }) {
  const toast = useToast();
  const [days, setDays] = useState(30);
  const [loading, setLoading] = useState(false);

  const approve = async () => {
    setLoading(true);
    try {
      const updated = await api.post(`/admin/accounts/${account.id}/subscription/approve`, { days: parseInt(days, 10) });
      onApproved(updated);
      toast.success(`Approved — ${account.name} is now active`);
    } catch (e) {
      toast.error(e.message);
    } finally {
      setLoading(false);
    }
  };

  const reject = async () => {
    setLoading(true);
    try {
      await api.post(`/admin/accounts/${account.id}/subscription/reject`, {});
      onRejected(account.id);
      toast.success(`Rejected — ${account.name} notified`);
    } catch (e) {
      toast.error(e.message);
    } finally {
      setLoading(false);
    }
  };

  const since = account.pending_since
    ? new Date(account.pending_since).toLocaleString()
    : '—';

  return (
    <div className="flex items-center justify-between p-4 rounded border border-amber-200 bg-amber-50 dark:border-amber-800/60 dark:bg-amber-900/10">
      <div>
        <p className="text-sm font-medium text-slate-800 dark:text-slate-100">{account.name}</p>
        <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
          Requested <span className="font-medium text-amber-700 dark:text-amber-400">{tierLabel(account.pending_tier)}</span>
          {' · '}{priceLabel(account)}
          {account.admin_email && <> · {account.admin_email}</>}
          {' · '}{since}
        </p>
      </div>
      <div className="flex items-center gap-2 shrink-0 ml-4">
        <input
          type="number"
          value={days}
          onChange={e => setDays(e.target.value)}
          min={1}
          disabled={loading}
          className="w-16 text-xs border border-slate-300 dark:border-slate-600 rounded px-2 py-1.5 bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-200"
          title="Days to grant"
        />
        <span className="text-xs text-slate-400">days</span>
        <button onClick={approve} disabled={loading}
          className="text-xs px-3 py-1.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded font-medium disabled:opacity-50">
          Approve
        </button>
        <button onClick={reject} disabled={loading}
          className="text-xs px-3 py-1.5 border border-red-300 dark:border-red-700 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded font-medium disabled:opacity-50">
          Reject
        </button>
      </div>
    </div>
  );
}

function AccountRow({ account, onUpdated, onDeleted }) {
  const toast = useToast();
  const [editing, setEditing] = useState(false);
  const [tier, setTier] = useState(account.subscription_tier || '');
  const [days, setDays] = useState(30);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      const updated = await api.put(`/admin/accounts/${account.id}/subscription`, {
        subscription_tier: tier || null,
        days: parseInt(days, 10),
      });
      onUpdated(updated);
      setEditing(false);
      toast.success('Subscription updated');
    } catch (e) {
      toast.error(e.message);
    } finally {
      setSaving(false);
    }
  };

  const extend = async (d) => {
    setSaving(true);
    try {
      const updated = await api.put(`/admin/accounts/${account.id}/subscription`, {
        subscription_tier: account.subscription_tier,
        days: d,
      });
      onUpdated(updated);
      toast.success(`Extended ${d} days`);
    } catch (e) {
      toast.error(e.message);
    } finally {
      setSaving(false);
    }
  };

  const deleteAccount = async () => {
    if (!window.confirm(`Delete account "${account.name}"? This cannot be undone.`)) return;
    setDeleting(true);
    try {
      await api.del(`/admin/accounts/${account.id}`);
      onDeleted(account.id);
      toast.success(`Account "${account.name}" deleted`);
    } catch (e) {
      toast.error(e.message);
    } finally {
      setDeleting(false);
    }
  };

  const days_remaining = daysLeft(account.subscription_expiration);

  return (
    <tr className="border-t border-slate-100 dark:border-slate-800">
      <td className="py-3 px-4">
        <p className="text-sm font-medium text-slate-800 dark:text-slate-100">{account.name}</p>
        {account.admin_email && (
          <p className="text-xs text-slate-400 dark:text-slate-500 mt-0.5">{account.admin_email}</p>
        )}
      </td>
      <td className="py-3 px-4">
        <StatusBadge tier={account.subscription_tier} expiration={account.subscription_expiration} />
      </td>
      <td className="py-3 px-4 text-xs text-slate-500 dark:text-slate-400">
        {account.subscription_tier ? priceLabel(account) : '—'}
      </td>
      <td className="py-3 px-4 text-xs text-slate-500 dark:text-slate-400">
        {account.subscription_start ? new Date(account.subscription_start).toLocaleDateString() : '—'}
      </td>
      <td className="py-3 px-4 text-xs text-slate-500 dark:text-slate-400">
        {account.subscription_expiration
          ? `${new Date(account.subscription_expiration).toLocaleDateString()} (${days_remaining}d)`
          : '—'}
      </td>
      <td className="py-3 px-4 text-xs text-slate-500 dark:text-slate-400 text-center">
        {account.user_count}
      </td>
      <td className="py-3 px-4">
        <div className="flex items-center gap-2">
          {editing ? (
            <>
              <select
                value={tier}
                onChange={e => setTier(e.target.value)}
                className="text-xs border border-slate-300 dark:border-slate-600 rounded px-2 py-1 bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-200"
              >
                <option value="">None</option>
                <option value={SELLER_PLAN}>{tierLabel(SELLER_PLAN)}</option>
              </select>
              {tier && (
                <input
                  type="number"
                  value={days}
                  onChange={e => setDays(e.target.value)}
                  min={1}
                  className="w-16 text-xs border border-slate-300 dark:border-slate-600 rounded px-2 py-1 bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-200"
                  placeholder="days"
                />
              )}
              <button onClick={save} disabled={saving}
                className="text-xs px-2 py-1 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50">
                {saving ? '…' : 'Save'}
              </button>
              <button onClick={() => setEditing(false)}
                className="text-xs text-slate-400 hover:text-slate-600 dark:hover:text-slate-200">
                Cancel
              </button>
            </>
          ) : (
            <>
              {account.subscription_tier && (
                <>
                  <button onClick={() => extend(30)} disabled={saving}
                    className="text-xs px-2 py-1 border border-slate-200 dark:border-slate-700 rounded hover:bg-slate-50 dark:hover:bg-slate-800 text-slate-600 dark:text-slate-300 disabled:opacity-50">
                    +30d
                  </button>
                  <button onClick={() => extend(90)} disabled={saving}
                    className="text-xs px-2 py-1 border border-slate-200 dark:border-slate-700 rounded hover:bg-slate-50 dark:hover:bg-slate-800 text-slate-600 dark:text-slate-300 disabled:opacity-50">
                    +90d
                  </button>
                </>
              )}
              <button
                onClick={() => { setTier(account.subscription_tier || ''); setDays(30); setEditing(true); }}
                className="text-xs px-2 py-1 border border-slate-200 dark:border-slate-700 rounded hover:bg-slate-50 dark:hover:bg-slate-800 text-slate-600 dark:text-slate-300">
                Edit
              </button>
            </>
          )}
          {account.user_count === 0 && (
            <button
              onClick={deleteAccount}
              disabled={deleting || saving}
              title="Delete account"
              className="p-1 rounded text-slate-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 disabled:opacity-40 disabled:cursor-not-allowed transition-colors ml-1"
            >
              {deleting
                ? <span className="w-3.5 h-3.5 border-2 border-current border-t-transparent rounded-full animate-spin inline-block" />
                : (
                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                      d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                  </svg>
                )
              }
            </button>
          )}
        </div>
      </td>
    </tr>
  );
}

const TABLE_HEAD = (
  <thead>
    <tr className="bg-slate-50 dark:bg-slate-800/50">
      <th className="py-2.5 px-4 text-left text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider">Account</th>
      <th className="py-2.5 px-4 text-left text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider">Status</th>
      <th className="py-2.5 px-4 text-left text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider">Price</th>
      <th className="py-2.5 px-4 text-left text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider">Started</th>
      <th className="py-2.5 px-4 text-left text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider">Expires</th>
      <th className="py-2.5 px-4 text-center text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider">Users</th>
      <th className="py-2.5 px-4 text-left text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider">Actions</th>
    </tr>
  </thead>
);

function AccountTable({ accounts, onUpdated, onDeleted, emptyText }) {
  return (
    <div className="card overflow-x-auto">
      <table className="w-full">
        {TABLE_HEAD}
        <tbody>
          {accounts.map(a => (
            <AccountRow key={a.id} account={a} onUpdated={onUpdated} onDeleted={onDeleted} />
          ))}
          {accounts.length === 0 && (
            <tr>
              <td colSpan={7} className="py-8 text-center text-sm text-slate-400">{emptyText}</td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
}

function SectionHeader({ label, count, color = 'slate' }) {
  const colors = {
    emerald: 'text-emerald-700 dark:text-emerald-400',
    slate:   'text-slate-500 dark:text-slate-400',
    red:     'text-red-600 dark:text-red-400',
  };
  return (
    <div className="flex items-center gap-3">
      <span className={`text-xs font-semibold uppercase tracking-wider ${colors[color]}`}>{label}</span>
      <span className="text-xs text-slate-400 dark:text-slate-500 font-medium">{count}</span>
      <div className="flex-1 h-px bg-slate-200 dark:bg-slate-700" />
    </div>
  );
}

export default function AdminSubscriptions() {
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await api.get('/admin/accounts');
      setAccounts(data);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleUpdated = useCallback((updated) => {
    setAccounts(prev => prev.map(a => a.id === updated.id ? { ...a, ...updated, pending_tier: null, pending_request_id: null } : a));
  }, []);

  const handleApproved = useCallback((updated) => {
    setAccounts(prev => prev.map(a => a.id === updated.id ? { ...a, ...updated, pending_tier: null, pending_request_id: null } : a));
  }, []);

  const handleRejected = useCallback((accountId) => {
    setAccounts(prev => prev.map(a => a.id === accountId ? { ...a, pending_tier: null, pending_request_id: null } : a));
  }, []);

  const handleDeleted = useCallback((accountId) => {
    setAccounts(prev => prev.filter(a => a.id !== accountId));
  }, []);

  if (loading) return (
    <div className="flex items-center justify-center h-64">
      <div className="w-5 h-5 border-2 border-blue-600 border-t-transparent rounded-full animate-spin" />
    </div>
  );

  if (error) return <div className="p-8 text-sm text-red-600">{error}</div>;

  const pending  = accounts.filter(a => a.pending_tier);
  // No subscription_expiration means the subscription never expires — treat it as active, not expired.
  const active   = accounts.filter(a => !a.pending_tier && a.subscription_tier && (daysLeft(a.subscription_expiration) === null || daysLeft(a.subscription_expiration) > 0));
  const expired  = accounts.filter(a => !a.pending_tier && a.subscription_tier && daysLeft(a.subscription_expiration) !== null && daysLeft(a.subscription_expiration) <= 0);
  const noSub    = accounts.filter(a => !a.pending_tier && !a.subscription_tier);

  return (
    <div className="space-y-8">
      <div className="flex items-center justify-between">
        <p className="text-sm text-slate-500 dark:text-slate-400">
          {pending.length > 0 && <span className="text-amber-600 dark:text-amber-400 font-medium">{pending.length} pending · </span>}
          {active.length} active · {expired.length} expired · {noSub.length} no subscription
        </p>
        <button onClick={load}
          className="text-xs text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 flex items-center gap-1">
          <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
              d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
          </svg>
          Refresh
        </button>
      </div>

      {pending.length > 0 && (
        <div className="space-y-3">
          <SectionHeader label="Pending Requests" count={pending.length} color="slate" />
          {pending.map(a => (
            <PendingRow key={a.id} account={a} onApproved={handleApproved} onRejected={handleRejected} />
          ))}
        </div>
      )}

      <div className="space-y-3">
        <SectionHeader label="Active" count={active.length} color="emerald" />
        <AccountTable accounts={active} onUpdated={handleUpdated} onDeleted={handleDeleted} emptyText="No active subscriptions" />
      </div>

      <div className="space-y-3">
        <SectionHeader label="Expired" count={expired.length} color="red" />
        <AccountTable accounts={expired} onUpdated={handleUpdated} onDeleted={handleDeleted} emptyText="No expired subscriptions" />
      </div>

      <div className="space-y-3">
        <SectionHeader label="No Subscription" count={noSub.length} color="slate" />
        <AccountTable accounts={noSub} onUpdated={handleUpdated} onDeleted={handleDeleted} emptyText="No unsubscribed accounts" />
      </div>
    </div>
  );
}
